import { useEffect, useRef, useState } from 'react'
import { fetchCameras } from '../api'
import type { CameraStatus, Site } from '../types'

interface Props {
  sites: Site[]
  current: { siteId: string; camera: string }
  health: Record<string, Record<string, CameraStatus>>
  onPick: (siteId: string, camera: string) => void
  /** Whether the filter shows a camera; every one when absent. */
  allows?: (siteId: string, camera: string) => boolean
}

// CameraPicker opens any camera from live view, grouped by site. The Sites
// sidebar kept every site's cameras one click away while watching; without it,
// changing site meant going back to the wall (#287).
export function CameraPicker({ sites, current, health, onPick, allows = () => true }: Props) {
  const [open, setOpen] = useState(false)
  const button = useRef<HTMLButtonElement>(null)
  const panel = useRef<HTMLDivElement>(null)
  const close = (refocus: boolean) => {
    setOpen(false)
    if (refocus) button.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    // Into the list, so the keyboard reaches it next and Escape closes it.
    panel.current?.focus()
    const onDown = (e: MouseEvent) => {
      if (!panel.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div className="relative">
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="h-9 px-3 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 text-sm font-semibold text-zinc-100 hover:bg-zinc-800"
      >
        <svg aria-hidden="true" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z" />
        </svg>
        Cameras
      </button>
      {open && (
        <div
          ref={panel}
          tabIndex={-1}
          aria-label="Cameras"
          onKeyDown={(e) => { if (e.key === 'Escape') close(true) }}
          className="absolute left-0 sm:left-auto sm:right-0 mt-2 z-50 w-72 max-w-[calc(100vw-2rem)] max-h-[min(28rem,calc(100dvh-8rem))] overflow-y-auto scrollbar-thin p-2 flex flex-col gap-3 rounded-lg border border-zinc-800 bg-zinc-900 shadow-[0_24px_48px_rgb(0_0_0/0.55)]"
        >
          {sites.map((s) => (
            <SiteCameras key={s.id} site={s} current={current} health={health[s.id] ?? {}} allows={(camera) => allows(s.id, camera)} onPick={(camera) => { close(false); onPick(s.id, camera) }} />
          ))}
        </div>
      )}
    </div>
  )
}

function SiteCameras({ site, current, health, allows, onPick }: {
  site: Site
  allows: (camera: string) => boolean
  current: Props['current']
  health: Record<string, CameraStatus>
  onPick: (camera: string) => void
}) {
  const [cameras, setCameras] = useState<string[] | null>(null)
  // Fetched as the list opens, as the filter panel does; the server caches
  // each site's list, so this crosses WireGuard at most once a minute.
  useEffect(() => {
    let cancelled = false
    fetchCameras(site.id)
      .then((c) => { if (!cancelled) setCameras(c ?? []) })
      .catch(() => { if (!cancelled) setCameras([]) })
    return () => { cancelled = true }
  }, [site.id])
  const name = site.name || site.id
  return (
    <div role="group" aria-label={name} className="flex flex-col gap-0.5">
      <span aria-hidden="true" className="px-2 pt-1 pb-1.5 text-xs font-mono uppercase tracking-[0.08em] text-zinc-400">{name}</span>
      {cameras === null ? (
        <span className="px-2 py-1.5 text-sm text-zinc-400">Loading…</span>
      ) : cameras.length === 0 ? (
        <span className="px-2 py-1.5 text-sm text-zinc-400">No cameras</span>
      ) : cameras.filter(allows).map((camera) => {
        const here = site.id === current.siteId && camera === current.camera
        const state = health[camera] === 'offline' ? 'offline' : health[camera] === 'degraded' ? 'degraded' : null
        return (
          <button
            key={camera}
            type="button"
            aria-current={here ? 'true' : undefined}
            aria-label={state ? `${camera}, ${state}` : camera}
            onClick={() => onPick(camera)}
            className={`min-h-9 px-2 flex items-center justify-between gap-2 rounded-md text-left text-sm ${here ? 'bg-orange-500/15 text-orange-300 font-semibold' : 'text-zinc-100 hover:bg-zinc-800'}`}
          >
            <span className="truncate">{camera}</span>
            {state && <span className={`shrink-0 text-xs ${state === 'offline' ? 'text-fault' : 'text-degraded'}`}>{state}</span>}
          </button>
        )
      })}
    </div>
  )
}
